import { Link } from 'react-router-dom';
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight, LogIn } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const CartPage = () => {
  const { cart, updateQuantity, removeFromCart, loading } = useCart();
  const { isAuthenticated } = useAuth();

  const handleUpdateQuantity = async (productId, newQuantity) => {
    try {
      await updateQuantity(productId, newQuantity);
    } catch (error) {
      toast.error('Failed to update quantity');
    }
  };

  const handleRemove = async (productId, productName) => {
    try {
      await removeFromCart(productId);
      toast.success(`${productName} removed from cart`);
    } catch (error) {
      toast.error('Failed to remove item');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-stone-50" data-testid="cart-page">
      <Navbar />
      
      <main className="flex-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h1 className="heading-section text-stone-900 mb-8" data-testid="cart-title">
            Shopping Cart
          </h1>

          {cart.items.length === 0 ? (
            <div className="empty-state bg-white rounded-2xl py-16">
              <ShoppingBag className="w-20 h-20 text-stone-300 mb-4" />
              <h3 className="text-xl font-semibold text-stone-900 mb-2">Your cart is empty</h3>
              <p className="text-stone-600 mb-6">Add some items to get started</p>
              <Link to="/products">
                <Button className="btn-primary" data-testid="continue-shopping">
                  Continue Shopping
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          ) : (
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Cart Items */}
              <div className="lg:col-span-2 space-y-4">
                {cart.items.map((item) => (
                  <div
                    key={item.product_id}
                    className="bg-white rounded-xl p-4 sm:p-6 flex gap-4 shadow-sm"
                    data-testid={`cart-item-${item.product_id}`}
                  >
                    {/* Image */}
                    <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-lg overflow-hidden bg-stone-100 flex-shrink-0">
                      {item.image_url ? (
                        <img 
                          src={item.image_url} 
                          alt={item.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-stone-400 text-sm">
                          No Image
                        </div>
                      )}
                    </div>

                    {/* Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between gap-2">
                        <Link 
                          to={`/product/${item.product_id}`}
                          className="font-semibold text-stone-900 hover:text-green-900 truncate"
                        >
                          {item.name}
                        </Link>
                        <button
                          onClick={() => handleRemove(item.product_id, item.name)}
                          className="text-stone-400 hover:text-red-600 flex-shrink-0"
                          data-testid={`remove-${item.product_id}`}
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>

                      {!item.in_stock && (
                        <span className="badge-error text-xs mt-1 inline-block">
                          Out of Stock
                        </span>
                      )}

                      <div className="mt-4 flex items-center justify-between">
                        {/* Quantity */}
                        <div className="flex items-center gap-2">
                          <button
                            className="qty-btn"
                            onClick={() => handleUpdateQuantity(item.product_id, item.quantity - 1)}
                            disabled={item.quantity <= 1}
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                          <span className="w-8 text-center font-semibold">
                            {item.quantity}
                          </span>
                          <button
                            className="qty-btn"
                            onClick={() => handleUpdateQuantity(item.product_id, item.quantity + 1)}
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>

                        {/* Price */}
                        <p className="font-bold text-green-900">
                          R{((item.selling_price || item.price) * item.quantity).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Order Summary */}
              <div className="lg:col-span-1">
                <div className="checkout-summary bg-white rounded-xl p-6 shadow-sm">
                  <h2 className="text-lg font-bold text-stone-900 mb-4">Order Summary</h2>
                  
                  <div className="space-y-3 pb-4 border-b border-stone-200">
                    <div className="flex justify-between text-stone-600">
                      <span>Subtotal ({cart.item_count} items)</span>
                      <span>R{cart.total.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-stone-600">
                      <span>Delivery</span>
                      <span className="text-green-600">Free</span>
                    </div>
                  </div>

                  <div className="flex justify-between py-4 text-lg font-bold text-stone-900">
                    <span>Total</span>
                    <span data-testid="cart-total">R{cart.total.toFixed(2)}</span>
                  </div>

                  {isAuthenticated ? (
                    <Link to="/checkout">
                      <Button 
                        className="w-full btn-primary py-6 text-lg" 
                        disabled={cart.items.some(item => item.in_stock === false)}
                        data-testid="checkout-btn"
                      >
                        Proceed to Checkout
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </Button>
                    </Link>
                  ) : (
                    <Link to="/auth?redirect=/checkout">
                      <Button 
                        className="w-full btn-primary py-6 text-lg"
                        data-testid="signin-to-checkout-btn"
                      >
                        Sign In to Checkout
                        <LogIn className="w-5 h-5 ml-2" />
                      </Button>
                    </Link>
                  )}

                  {cart.items.some(item => !item.in_stock) && (
                    <p className="text-sm text-red-600 mt-3 text-center">
                      Please remove out-of-stock items to proceed
                    </p>
                  )}

                  <Link to="/products" className="block text-center mt-4 text-green-900 hover:underline">
                    Continue Shopping
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CartPage;
